import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/shared/lib/cn';

export type ConfirmDialogProps = {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
  pending?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
};

/**
 * Modal de confirmacao acessivel (sem dependencia de Radix). Usado em
 * acoes destrutivas como apagar mensagem em lote no chat. Mobile-first:
 * card centralizado, tap fora fecha, Esc cancela.
 */
export const ConfirmDialog = ({
  open,
  title,
  description,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  destructive = false,
  pending = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) => {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Foco inicial no botao primario (padrao iOS/Android pra acoes
    // perigosas: usuario precisa confirmar conscientemente).
    confirmRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  const handleKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && !pending) {
      e.preventDefault();
      onCancel();
    }
  };

  const handleBackdrop = () => {
    if (!pending) onCancel();
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4 animate-in fade-in duration-150"
      onClick={handleBackdrop}
      onKeyDown={handleKey}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl"
      >
        <h2 id="confirm-dialog-title" className="text-base font-semibold text-gray-900">
          {title}
        </h2>
        {description ? (
          <div className="mt-2 text-sm leading-relaxed text-gray-500">{description}</div>
        ) : null}
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            className="inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={() => void onConfirm()}
            disabled={pending}
            className={cn(
              'inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold text-white transition-colors disabled:opacity-60',
              destructive
                ? 'bg-red-600 hover:bg-red-700 focus-visible:ring-red-700'
                : 'bg-gray-900 hover:bg-black focus-visible:ring-gray-900',
            )}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
